import { FC, useEffect } from "react";
import {
  ApproveNominationCommitteeRemunerationForm,
  RemunerationPeriod,
} from "./approveNominationCommitteeRemunerationDomain";
import { Box, VBox } from "@/components-v2/Box";
import { Button, IconButton } from "@/components-v2/Button";
import { observer } from "mobx-react";
import { NumberField, YesNoQuestion } from "@/dbot/common/commonComponents";
import { Alert } from "@/components-v2/Alert";
import { Checkbox } from "@/components-v2/Checkbox";
import { Dialog } from "@/components-v2/Dialog";
import { MTextInput } from "@/components-v2/TextInput";
import { CURRENCY_NOK } from "@/utils/currency";

import { useTranslation } from "@/stores/RootStoreContext";

export const ApproveNominationCommitteeRemunerationView: FC<{
  form: ApproveNominationCommitteeRemunerationForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();

  useEffect(() => {
    form.init();
  }, [form]);
  return (
    <VBox gap={"md"}>
      <YesNoQuestion
        question={t("decisions.approveNominationCommitteeRemuneration.who-receive-remuneration")}
        field={form.isReceive}
        isLocked={form.isProjectLocked}
      />
      {form.isReceive.value && (
        <VBox>
          <div>{t("decisions.approveNominationCommitteeRemuneration.what-period.question")}</div>
          {form.remunerationPeriodValidationMessage && (
            <Alert message={t(form.remunerationPeriodValidationMessage)} type={"warning"} />
          )}
          <Box>
            <Checkbox
              key={RemunerationPeriod.untilNextGeneralMeeting}
              label={t("decisions.approveNominationCommitteeRemuneration.what-period.true")}
              onChange={() =>
                form.remunerationPeriods.toggle(RemunerationPeriod.untilNextGeneralMeeting)
              }
              checked={form.remunerationPeriods.isSelected(
                RemunerationPeriod.untilNextGeneralMeeting,
              )}
              name={"RemunerationPeriod.untilNextGeneralMeeting"}
              disabled={form.isProjectLocked}
            />
          </Box>
          {form.remunerationPeriods.value.includes(RemunerationPeriod.untilNextGeneralMeeting) && (
            <>
              {form.newNominationCommittee.map((newMember) => {
                return (
                  <VBox key={newMember.id} marginLeft={"lg"}>
                    <Box>
                      <div>
                        {t("decisions.approveNominationCommitteeRemuneration.remuneration-to", {
                          name: newMember.name,
                        })}
                      </div>
                    </Box>
                    <NumberField
                      field={newMember.valueField}
                      prefix={CURRENCY_NOK}
                      isLocked={form.isProjectLocked}
                      inline
                    />
                  </VBox>
                );
              })}
              {(!form.hasElectNominationCommitteeDecision ||
                (form.hasElectNominationCommitteeDecision &&
                  form.newNominationCommittee.length === 0)) && (
                <Box>
                  <Alert
                    icon={{ name: "warning", size: "lg" }}
                    type="warning"
                    message={
                      <Box>
                        {t(
                          "decisions.approveNominationCommitteeRemuneration.what-period.true.alert",
                        )}

                        {!form.hasElectNominationCommitteeDecision && (
                          <Button
                            label={t(
                              "decisions.approveNominationCommitteeRemuneration.what-period.true.add-decision",
                            )}
                            variant={"secondary"}
                            size={"sm"}
                            onClick={form.addElectNominationCommitteeDecision}
                            disabled={form.isProjectLocked}
                          />
                        )}
                      </Box>
                    }
                  />
                </Box>
              )}
            </>
          )}
          <Box>
            <Checkbox
              key={RemunerationPeriod.fromLastGeneralMeetingToCurrent}
              label={t("decisions.approveNominationCommitteeRemuneration.what-period.false")}
              onChange={() =>
                form.remunerationPeriods.toggle(RemunerationPeriod.fromLastGeneralMeetingToCurrent)
              }
              checked={form.remunerationPeriods.isSelected(
                RemunerationPeriod.fromLastGeneralMeetingToCurrent,
              )}
              name={"RemunerationPeriod.fromLastGeneralMeetingToCurrent"}
              disabled={form.isProjectLocked}
            />
          </Box>
          {form.remunerationPeriods.value.includes(
            RemunerationPeriod.fromLastGeneralMeetingToCurrent,
          ) && (
            <>
              {form.nominationCommitteeRemuneration?.map((byName) => {
                return (
                  <Box key={byName.id} marginLeft={"lg"} gap={"md"} alignItems={"end"}>
                    <NumberField
                      field={byName.valueField}
                      label={t("decisions.approveNominationCommitteeRemuneration.remuneration-to", {
                        name: byName.name,
                      })}
                      prefix={CURRENCY_NOK}
                      isLocked={form.isProjectLocked}
                      inline
                    />
                    <IconButton
                      icon={"trash"}
                      tooltip={t("remove")}
                      onClick={() => {
                        form.removeNominationCommitteeByName(byName.id);
                      }}
                    />
                  </Box>
                );
              })}
              <Box key="buttonNominationCommitteeRemunerationByName">
                <Button
                  label={t("decisions.approveNominationCommitteeRemuneration.add-member")}
                  variant={"secondary"}
                  fullWidth={true}
                  icon={"plus"}
                  onClick={() => form.displayAddNewMemberDialog()}
                  disabled={form.isProjectLocked}
                />
              </Box>
              {form.nominationCommitteeRemuneration.length === 0 && (
                <Alert message={t("required")} type={"warning"} />
              )}
            </>
          )}
          {form.isDisplayAddNewMemberDialog && (
            <Dialog
              title={t("decisions.approveNominationCommitteeRemuneration.add-member")}
              submitLabel={t("decisions.save")}
              onSubmit={() => form.addNewMemberAndCloseDialog()}
              onCancel={() => form.hideAddNewMemberDialog()}
            >
              <MTextInput label={t("decisions.name")} name={"name"} model={form.newMemberName} />
            </Dialog>
          )}
        </VBox>
      )}
    </VBox>
  );
});
